/**
 * purchaseInvoiceCoreRenderers.tsx — F16 E3 (ujednolicenie z Companies/Pracownicy).
 *
 * Mapa core slug → renderer dla 24 pól Faktury zakupu w zakładce „Dane".
 * Analog do rendererów Firmy (`companyEngineConfig.tsx`). Reuse `Section` + `Field` z
 * `CoreFieldLayout` (te same layout primitives we wszystkich modułach
 * systemowych — parytet UI).
 *
 * Special handling per pole:
 *  - `vendor_id` — picker z listy kontraktorów (`/api/contractors`). Cache 60s.
 *  - `document_type` / `urgency` / `payment_method` — select z DB dictionary
 *    (`useDictionary`, Etap 4d/F12.D2). `status` / `currency_code` zostają
 *    listami kodów z `@saas/shared` (brak parytetu z seeded dictionary / brak
 *    dictionary w ogóle — patrz komentarz przy sekcji „Słowniki”).
 *  - `issue_date` / `received_date` / `sale_date` / `payment_due_date` /
 *    `paid_at` — `<input type="date">` (ISO YYYY-MM-DD).
 *  - `amount_net` / `amount_vat` / `amount_gross` / `exchange_rate` — number
 *    z `step="0.01"` (4 dla exchangeRate).
 *  - `issue_date` / `received_date` / `payment_due_date` / `amount_net` / `amount_gross` —
 *    mimo typu `string` (niepusty) w `PurchaseInvoiceFormData`, backend potrafi zwrócić
 *    `null` dla faktur zaimportowanych bez tych danych (KSeF/Symfonia — kolumny DB są
 *    nullable). `value={... ?? ''}` trzyma input kontrolowany od pierwszego renderu —
 *    bez tego React ostrzega "changing an uncontrolled input to be controlled". Stan
 *    `form` i payload zapisu NIE są dotykane (`?? ''` jest tylko w warstwie wyświetlania).
 *  - `system_number` — read-only, auto-gen przez backend.
 *  - `vendor_tax_id_cached` / `vendor_name_cached` — read-only, denorm cache z
 *    kartoteki dostawcy (set'owane backendem przy POST/PATCH).
 *  - `notes` / `payment_notes` / `rejection_reason` — textarea.
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT_RECEIPT_CHANNELS,
  INVOICE_CURRENCIES,
  INVOICE_STATUSES,
  JPK_DOCUMENT_MARKERS,
  KSEF_ISSUE_MODES,
  PURCHASE_EVIDENCE_MARKERS,
} from '@saas/shared';

import { Checkbox } from '@/components/Checkbox';
import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS_MUTED_DISABLED } from '@/components/coreRendererClasses';
import { CodedSelect, formatTimestampPl } from '@/components/documents/codedFieldControls';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import {
  buildVendorAutoFillPatch,
  VendorTablePickerButton,
} from '@/components/invoices/vendorPicker';
import { type CoreFieldMeta, Field, Section } from '@/components/kartoteki/CoreFieldLayout';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { ContractorSearchPicker } from '@/components/ui/ContractorSearchPicker';
import { OpenRecordCardButton } from '@/components/ui/OpenRecordCardButton';
import { UserPicker } from '@/components/ui/UserPicker';
import { UserTablePickerButton } from '@/components/ui/UserTablePickerButton';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { invoiceStatusLabel } from '@/lib/documents/invoice-select-labels';
import {
  documentReceiptChannelLabel,
  jpkDocumentMarkerLabel,
  ksefIssueModeLabel,
  purchaseEvidenceMarkerLabel,
} from '@/lib/documents/ksef-jpk-markers';

import type { PurchaseInvoiceFormData } from './purchaseInvoiceFormTypes';
import { SourceBadge } from './SourceBadge';

export { Field, Section };

export interface PurchaseInvoiceRendererProps {
  field: CoreFieldMeta;
  form: PurchaseInvoiceFormData;
  set: <K extends keyof PurchaseInvoiceFormData>(key: K, value: PurchaseInvoiceFormData[K]) => void;
  /** Atomowy zapis wielu pól w jednym `onChange` (unika nadpisywania się kolejnych `set`). */
  setMany: (patch: Partial<PurchaseInvoiceFormData>) => void;
  /** Czy dane pole jest oznaczone jako nieprawidłowe (ramka) — puste wymagane / zły format / odrzucone przez serwer. */
  error: (key: keyof PurchaseInvoiceFormData) => boolean;
  /** Komunikat błędu pod polem (czerwony tekst) — konkretny powód po polsku; `undefined` = brak błędu. */
  errorText: (key: keyof PurchaseInvoiceFormData) => string | undefined;
  editable: boolean;
  isCreate: boolean;
  /** Dokument ma pozycje → kwoty nagłówka wyliczane serwerowo z linii, pola kwot read-only. */
  hasItems?: boolean;
  /**
   * Firma wybrana w create (z URL, patrz PurchaseInvoiceDetail) — przekazywana jawnie do
   * `ContractorSearchPicker` (`vendor_id`), żeby picker w trybie „Wszystkie firmy" pokazał
   * kontrahentów WŁAŚCIWEJ firmy, nie unii wszystkich firm usera. `undefined`/`null` w edit
   * (bez zmiany zachowania — firma pochodzi wtedy z rekordu, nie z tego propa).
   */
  companyId?: string | null;
}

type Renderer = (props: PurchaseInvoiceRendererProps) => ReactNode;

// ─── Słowniki ────────────────────────────────────────────────────────────
//
// F12.D2 (Etap 4d): document_type / urgency / payment_method czytane z DB
// dictionaries (`purchase_invoice_document_type` / `purchase_invoice_urgency` /
// `purchase_invoice_payment_method`) przez `useDictionary`. Zapisywana wartość
// to nadal item CODE (zero migracji danych) — patrz `DictionarySelect` niżej.
// Etykieta opcji formatowana przez `formatDictionaryOptionLabel` (Etap 4b,
// `@/hooks/useDictionary`) — wspólny formatter displayMode label/code/code_label.
//
// Status (`INVOICE_STATUSES` z `@saas/shared`) zostaje listą z kodu: seeded dictionary
// `purchase_invoice_status` ma tylko 5 kodów (draft/received/approved/paid/rejected), ale UI/testy
// zakładają też `correction` — kody NIE są w parytecie, przełączenie zgubiłoby wartość
// `correction` z selecta. Waluty (`INVOICE_CURRENCIES`) też z kodu: brak seeded dictionary dla
// walut; waluta pokazuje sam kod („PLN”). Nazwy statusów: `invoiceStatusLabel`.

// ─── Helpery sub-komponenty (analog rendererów Firmy, companyEngineConfig) ──────

const inputClass = CORE_INPUT_CLASS_MUTED_DISABLED;

// Klasa nakładana na pole z błędem walidacji (puste pole wymagane przy zapisie).
const invalidInputClass = 'border-red-500 ring-1 ring-red-500';

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput variant="mutedDisabled" {...props} />;
}

function Textarea({
  value,
  onChange,
  disabled,
  testId,
  rows = 3,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  testId?: string;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      data-testid={testId}
      className={inputClass}
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
  labelFor,
  disabled,
  testId,
  withEmpty = false,
  emptyLabel = '— wybierz —',
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  /** Nazwa kodu na liście; bez niej opcja pokazuje sam kod (waluta). */
  labelFor?: (code: string, t: (key: string) => string) => string;
  disabled?: boolean;
  testId?: string;
  withEmpty?: boolean;
  emptyLabel?: string;
  invalid?: boolean;
}) {
  // Hook w pod-komponencie, bo czyste lambdy rendererów nie mogą go wywołać.
  const { t } = useTranslation();
  return (
    <Dropdown
      options={[
        ...(withEmpty ? [{ value: '', label: emptyLabel }] : []),
        ...options.map((opt) => ({ value: opt, label: labelFor ? labelFor(opt, t) : opt })),
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? invalidInputClass : undefined}
    />
  );
}

/** Podpowiedź pod polem — literalny klucz `t()`, żeby skaner i18n ją widział. */
function EvidenceMarkerHint() {
  const { t } = useTranslation();
  return (
    <p className="mt-1 text-xs text-text-muted">
      {t('tenant.purchaseInvoices.ksef.evidenceMarkerHint')}
    </p>
  );
}

/** Rozróżnia „sposób otrzymania" od plakietki „Źródło" w nagłówku karty. */
function ReceiptChannelHint() {
  const { t } = useTranslation();
  return (
    <p className="mt-1 text-xs text-text-muted">
      {t('tenant.purchaseInvoices.ksef.receiptChannelHint')}
    </p>
  );
}

// Sub-komponent z hokiem useDictionary — czyste lambdy nie mogą wywoływać hooków
// (wzorzec jak ObjectTypeSelect / StatusSelect w contractorCoreRenderers).
// Select generyczny dla dowolnego DB dictionary — value zapisywany/odczytywany
// to zawsze item CODE (zero migracji danych). Zachowuje już zapisany kod nawet
// gdy jego pozycja stała się nieaktywna (dołączany jako dodatkowa opcja).
function DictionarySelect({
  dictionaryCode,
  value,
  onChange,
  disabled,
  invalid,
  testId,
  withEmpty = false,
  emptyLabel = '— wybierz —',
}: {
  dictionaryCode: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  testId?: string;
  withEmpty?: boolean;
  emptyLabel?: string;
}) {
  const { dictionary, activeItems, itemsByCode } = useDictionary(dictionaryCode);
  const displayMode = dictionary?.displayMode ?? 'label';

  // Wartość już zapisana, ale jej pozycja stała się nieaktywna/ukryta —
  // nadal pokazujemy ją w select (jako jedyną nie-selectable opcję), żeby nie
  // gubić danych na formularzu istniejącego rekordu. Backend traktuje
  // porównania kodów jako case-insensitive (patrz `merge.ts`) — global/legacy
  // kody bywają mixed-case — więc lookup po `itemsByCode` też musi ignorować
  // wielkość liter, inaczej wartość różniąca się tylko wielkością liter
  // wygląda jak nieobecna (pokazuje surowy kod zamiast etykiety / dokłada
  // zbędną „stale" opcję mimo że pozycja istnieje).
  const lowerValue = value.toLowerCase();
  const currentItem = value
    ? (itemsByCode.get(value) ??
      Array.from(itemsByCode.values()).find((item) => item.code.toLowerCase() === lowerValue))
    : undefined;
  // Gdy trafiony item ma inną wielkość liter niż zapisany `value` (np. item
  // 'VAT', zapisane 'vat'), <option value={item.code}> nie dopasuje się do
  // <select value={value}> (porównanie stringów w DOM jest case-sensitive) —
  // renderujemy opcję z surowym `value`, żeby select pokazał właściwą etykietę
  // i pozostał kontrolowany, bez zmiany zapisywanej wartości.
  const currentItemCaseMismatch = !!currentItem && currentItem.code !== value;
  const showStaleCurrent = !!currentItem && !currentItem.selectable;

  const dropdownOptions: DropdownOption[] = [
    ...(withEmpty ? [{ value: '', label: emptyLabel }] : []),
    ...(showStaleCurrent && currentItem
      ? [{ value, label: formatDictionaryOptionLabel(currentItem, displayMode) }]
      : []),
    ...(!showStaleCurrent && currentItemCaseMismatch && currentItem
      ? [{ value, label: formatDictionaryOptionLabel(currentItem, displayMode) }]
      : []),
    ...activeItems.map((item) => ({
      value: item.code,
      label: formatDictionaryOptionLabel(item, displayMode),
    })),
  ];

  return (
    <Dropdown
      options={dropdownOptions}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
      className={invalid ? invalidInputClass : undefined}
    />
  );
}

// ─── Mapa renderers (slug → JSX) ────────────────────────────────────────────

export const PURCHASE_INVOICE_CORE_RENDERERS: Record<string, Renderer> = {
  // — Identyfikacja —
  system_number: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-systemNumber"
        value={p.form.systemNumber ?? ''}
        onChange={() => {}}
        disabled
        placeholder={p.isCreate ? 'Nadany po zapisie' : ''}
      />
    </Field>
  ),

  invoice_number: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('invoiceNumber')}>
      <TextInput
        testId="pi-field-invoiceNumber"
        value={p.form.invoiceNumber}
        onChange={(v) => p.set('invoiceNumber', v)}
        disabled={!p.editable}
        maxLength={50}
        invalid={p.error('invoiceNumber')}
      />
    </Field>
  ),

  ksef_number: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-ksefNumber"
        value={p.form.ksefNumber ?? ''}
        onChange={(v) => p.set('ksefNumber', v || null)}
        disabled={!p.editable}
        maxLength={40}
      />
    </Field>
  ),

  document_type: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('documentType')}>
      <DictionarySelect
        dictionaryCode="purchase_invoice_document_type"
        testId="pi-field-documentType"
        value={p.form.documentType}
        onChange={(v) => p.set('documentType', v)}
        disabled={!p.editable}
        invalid={p.error('documentType')}
      />
    </Field>
  ),

  status: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('status')}>
      <SelectInput
        testId="pi-field-status"
        value={p.form.status}
        onChange={(v) => p.set('status', v)}
        options={INVOICE_STATUSES}
        labelFor={invoiceStatusLabel}
        disabled={!p.editable}
        invalid={p.error('status')}
      />
    </Field>
  ),

  urgency: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('urgency')}>
      <DictionarySelect
        dictionaryCode="purchase_invoice_urgency"
        testId="pi-field-urgency"
        value={p.form.urgency}
        onChange={(v) => p.set('urgency', v)}
        disabled={!p.editable}
        invalid={p.error('urgency')}
      />
    </Field>
  ),

  corrected_invoice_id: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-correctedInvoiceId"
        value={p.form.correctedInvoiceId ?? ''}
        onChange={(v) => p.set('correctedInvoiceId', v || null)}
        disabled={!p.editable}
        placeholder="UUID faktury pierwotnej (jeśli korekta)"
      />
    </Field>
  ),

  // — Daty —
  issue_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('issueDate')}>
      <TextInput
        testId="pi-field-issueDate"
        type="date"
        value={p.form.issueDate ?? ''}
        onChange={(v) => p.set('issueDate', v)}
        disabled={!p.editable}
        invalid={p.error('issueDate')}
      />
    </Field>
  ),

  received_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('receivedDate')}>
      <TextInput
        testId="pi-field-receivedDate"
        type="date"
        value={p.form.receivedDate ?? ''}
        onChange={(v) => p.set('receivedDate', v)}
        disabled={!p.editable}
        invalid={p.error('receivedDate')}
      />
    </Field>
  ),

  sale_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-saleDate"
        type="date"
        value={p.form.saleDate ?? ''}
        onChange={(v) => p.set('saleDate', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  payment_due_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('paymentDueDate')}>
      <TextInput
        testId="pi-field-paymentDueDate"
        type="date"
        value={p.form.paymentDueDate ?? ''}
        onChange={(v) => p.set('paymentDueDate', v)}
        disabled={!p.editable}
        invalid={p.error('paymentDueDate')}
      />
    </Field>
  ),

  // — Dostawca —
  // T3: dwie ścieżki wyboru (inline type-ahead + przycisk otwierający tabelowy
  // picker rekordów), OBIE wołają WSPÓLNY helper `buildVendorAutoFillPatch`
  // (patrz `components/invoices/vendorPicker.tsx`) — jeden kontrakt zapisu
  // cache'u nazwy/NIP dla obu ścieżek i obu klas faktur.
  vendor_id: (p) => (
    <Field
      label={p.field.name}
      required={p.field.isRequired}
      colSpan={2}
      error={p.errorText('vendorId')}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <ContractorSearchPicker
            data-testid="pi-field-vendor"
            value={p.form.vendorId}
            selectedLabel={p.form.vendorNameCached}
            companyId={p.companyId}
            onChange={(id, vendor) => p.setMany(buildVendorAutoFillPatch(id, vendor))}
            disabled={!p.editable}
            invalid={p.error('vendorId')}
          />
        </div>
        <VendorTablePickerButton
          companyId={p.companyId}
          value={p.form.vendorId}
          disabled={!p.editable}
          onPick={(id, vendor) => p.setMany(buildVendorAutoFillPatch(id, vendor))}
          data-testid="pi-field-vendor-open-picker"
        />
        <OpenRecordCardButton
          systemSlug="contractors"
          recordId={p.form.vendorId}
          testId="pi-field-vendor-open-card"
        />
      </div>
    </Field>
  ),

  vendor_tax_id_cached: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-vendorTaxIdCached"
        value={p.form.vendorTaxIdCached ?? ''}
        onChange={(v) => p.set('vendorTaxIdCached', v || null)}
        disabled={!p.editable}
        maxLength={20}
      />
    </Field>
  ),

  vendor_name_cached: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-vendorNameCached"
        value={p.form.vendorNameCached ?? ''}
        onChange={(v) => p.set('vendorNameCached', v || null)}
        disabled={!p.editable}
        maxLength={500}
      />
    </Field>
  ),

  // — Kwoty —
  currency_code: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('currencyCode')}>
      <SelectInput
        testId="pi-field-currency"
        value={p.form.currencyCode}
        onChange={(v) => p.set('currencyCode', v)}
        options={INVOICE_CURRENCIES}
        disabled={!p.editable}
        invalid={p.error('currencyCode')}
      />
    </Field>
  ),

  exchange_rate: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-exchangeRate"
        type="number"
        step="0.0001"
        min="0"
        value={p.form.exchangeRate ?? ''}
        onChange={(v) => p.set('exchangeRate', v || null)}
        disabled={!p.editable}
        placeholder={p.form.currencyCode === 'PLN' ? 'Nie wymagany dla PLN' : ''}
      />
    </Field>
  ),

  amount_net: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('amountNet')}>
      <TextInput
        testId="pi-field-amountNet"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountNet ?? ''}
        onChange={(v) => p.set('amountNet', v)}
        disabled={!p.editable || !!p.hasItems}
        invalid={p.error('amountNet')}
      />
    </Field>
  ),

  amount_vat: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-amountVat"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountVat ?? ''}
        onChange={(v) => p.set('amountVat', v || null)}
        disabled={!p.editable || !!p.hasItems}
      />
    </Field>
  ),

  amount_gross: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} error={p.errorText('amountGross')}>
      <TextInput
        testId="pi-field-amountGross"
        type="number"
        step="0.01"
        min="0"
        value={p.form.amountGross ?? ''}
        onChange={(v) => p.set('amountGross', v)}
        disabled={!p.editable || !!p.hasItems}
        invalid={p.error('amountGross')}
      />
    </Field>
  ),

  // — Płatność —
  payment_method: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <DictionarySelect
        dictionaryCode="purchase_invoice_payment_method"
        testId="pi-field-paymentMethod"
        value={p.form.paymentMethod ?? ''}
        onChange={(v) => p.set('paymentMethod', v || null)}
        disabled={!p.editable}
        withEmpty
      />
    </Field>
  ),

  paid_at: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-paidAt"
        type="date"
        value={p.form.paidAt ?? ''}
        onChange={(v) => p.set('paidAt', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  vendor_bank_account_id: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-vendorBankAccountId"
        value={p.form.vendorBankAccountId ?? ''}
        onChange={(v) => p.set('vendorBankAccountId', v || null)}
        disabled={!p.editable}
        placeholder="UUID konta bankowego (T16.8 picker)"
      />
    </Field>
  ),

  payment_notes: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="pi-field-paymentNotes"
        value={p.form.paymentNotes ?? ''}
        onChange={(v) => p.set('paymentNotes', v || null)}
        disabled={!p.editable}
        rows={2}
      />
    </Field>
  ),

  // — Inne —
  description: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <TextInput
        testId="pi-field-description"
        value={p.form.description ?? ''}
        onChange={(v) => p.set('description', v || null)}
        disabled={!p.editable}
        placeholder="Krótki tytuł (np. Hosting + domena)"
      />
    </Field>
  ),

  cost_center_default: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-costCenterDefault"
        value={p.form.costCenterDefault ?? ''}
        onChange={(v) => p.set('costCenterDefault', v || null)}
        disabled={!p.editable}
        maxLength={50}
      />
    </Field>
  ),

  rejection_reason: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="pi-field-rejectionReason"
        value={p.form.rejectionReason ?? ''}
        onChange={(v) => p.set('rejectionReason', v || null)}
        disabled={!p.editable}
        rows={2}
      />
    </Field>
  ),

  notes: (p) => (
    <Field label={p.field.name} required={p.field.isRequired} colSpan={2}>
      <Textarea
        testId="pi-field-notes"
        value={p.form.notes ?? ''}
        onChange={(v) => p.set('notes', v || null)}
        disabled={!p.editable}
        rows={3}
      />
    </Field>
  ),

  // — Opiekun rekordu (T20D, parytet z umowy/pisma/handlowe/wewnętrzne) —
  owner_user_id: (p) => (
    <Field label={p.field.name} required={false}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <UserPicker
            value={p.form.ownerUserId}
            onChange={(v) => p.set('ownerUserId', v)}
            companyId={p.companyId}
            disabled={!p.editable}
            data-testid="pi-field-ownerUserId"
          />
        </div>
        <UserTablePickerButton
          value={p.form.ownerUserId}
          onPick={(id) => p.set('ownerUserId', id)}
          companyId={p.companyId}
          disabled={!p.editable}
          data-testid="pi-field-ownerUserId-open-picker"
        />
      </div>
    </Field>
  ),

  // — Kategoria (oś `record_categories`, kartoteka_key='documents' — wspólna dla
  //   wszystkich 7 klas dokumentów). RecordCategoryPicker rysuje własny <Field>
  //   (label + dropdown), więc renderer NIE owija go drugi raz (mirror
  //   documentCategoryRenderer.tsx — kontrakt InvoiceRenderer używa `set`, nie
  //   engine `setField`, więc adapter jest lokalny do tego rejestru).
  category_id: (p) => (
    <RecordCategoryPicker
      kartotekaKey="documents"
      value={p.form.categoryId}
      onChange={(next) => p.set('categoryId', next)}
      editable={p.editable}
      testId="pi-field-category"
    />
  ),

  // — Źródło (T21 finding CRITICAL: commonField('source'/'external_id'/'external_source')
  //   trafia do FORM layoutu, ale front nie miał renderera — żółty warning „Typ pola nie
  //   jest obsługiwany w formularzu"). Read-only: PATCH_BODY_SCHEMA (route.ts) nie przyjmuje
  //   tych pól — `source` ustawia backend przy tworzeniu ('manual') lub imporcie ERP/KSeF
  //   ('symfonia'/'ksef', patrz lib/erp-integrations/map-symfonia-purchase-invoice.ts i
  //   lib/ksef/create-invoice-document.ts); `external_id`/`external_source` to identyfikatory
  //   idempotency importu. `source` reużywa tego samego badge'a co nagłówek strony (SourceBadge)
  //   — zero duplikacji i18n/kolorów, poprawnie pokazuje symfonia/ksef (bare-class SourceSelect
  //   ma tylko manual/import/api — nie pasuje do realnych wartości faktur).
  source: (p) => (
    <Field label={p.field.name} required={false}>
      <SourceBadge source={p.form.source} />
    </Field>
  ),

  external_id: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-externalId"
        value={p.form.externalId ?? ''}
        onChange={() => {}}
        disabled
        maxLength={100}
      />
    </Field>
  ),

  external_source: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-externalSource"
        value={p.form.externalSource ?? ''}
        onChange={() => {}}
        disabled
        maxLength={50}
      />
    </Field>
  ),

  // ── Sekcja: KSeF i ewidencja VAT (migracja 0159) ─────────────────────────────
  ksef_issue_date: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <TextInput
        testId="pi-field-ksefIssueDate"
        type="date"
        value={p.form.ksefIssueDate ?? ''}
        onChange={(v) => p.set('ksefIssueDate', v || null)}
        disabled={!p.editable}
      />
    </Field>
  ),

  purchase_evidence_marker: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <CodedSelect
        testId="pi-field-purchaseEvidenceMarker"
        value={p.form.purchaseEvidenceMarker ?? ''}
        onChange={(v) => p.set('purchaseEvidenceMarker', v || null)}
        codes={PURCHASE_EVIDENCE_MARKERS}
        labelFor={purchaseEvidenceMarkerLabel}
        disabled={!p.editable}
      />
      <EvidenceMarkerHint />
    </Field>
  ),

  jpk_document_marker: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <CodedSelect
        testId="pi-field-jpkDocumentMarker"
        value={p.form.jpkDocumentMarker ?? ''}
        onChange={(v) => p.set('jpkDocumentMarker', v || null)}
        codes={JPK_DOCUMENT_MARKERS}
        labelFor={jpkDocumentMarkerLabel}
        disabled={!p.editable}
      />
    </Field>
  ),

  goods_import: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <Checkbox
        data-testid="pi-field-goodsImport"
        checked={p.form.goodsImport}
        onChange={(e) => p.set('goodsImport', e.target.checked)}
        disabled={!p.editable}
      />
    </Field>
  ),

  ksef_issue_mode: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <CodedSelect
        testId="pi-field-ksefIssueMode"
        value={p.form.ksefIssueMode ?? ''}
        onChange={(v) => p.set('ksefIssueMode', v || null)}
        codes={KSEF_ISSUE_MODES}
        labelFor={ksefIssueModeLabel}
        disabled={!p.editable}
      />
    </Field>
  ),

  document_receipt_channel: (p) => (
    <Field label={p.field.name} required={p.field.isRequired}>
      <CodedSelect
        testId="pi-field-documentReceiptChannel"
        value={p.form.documentReceiptChannel ?? ''}
        onChange={(v) => p.set('documentReceiptChannel', v || null)}
        codes={DOCUMENT_RECEIPT_CHANNELS}
        labelFor={documentReceiptChannelLabel}
        disabled={!p.editable}
      />
      <ReceiptChannelHint />
    </Field>
  ),

  // Trójka z importu KSeF — `disabled` BEZ oglądania się na `p.editable` (wzorzec
  // `external_id`/`external_source` wyżej). Trasy nie przyjmują tych pól w body, a katalog
  // ma je oznaczone jako tylko-do-odczytu; to trzecia, widoczna dla usera linia obrony.
  ksef_submitted_at: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-ksefSubmittedAt"
        value={formatTimestampPl(p.form.ksefSubmittedAt)}
        onChange={() => {}}
        disabled
      />
    </Field>
  ),

  ksef_available_at: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-ksefAvailableAt"
        value={formatTimestampPl(p.form.ksefAvailableAt)}
        onChange={() => {}}
        disabled
      />
    </Field>
  ),

  ksef_source_ip: (p) => (
    <Field label={p.field.name} required={false}>
      <TextInput
        testId="pi-field-ksefSourceIp"
        value={p.form.ksefSourceIp ?? '—'}
        onChange={() => {}}
        disabled
        maxLength={45}
      />
    </Field>
  ),
};

export function hasPurchaseInvoiceCoreRenderer(slug: string): boolean {
  return slug in PURCHASE_INVOICE_CORE_RENDERERS;
}
